import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import SiteHead from "@/components/SiteHead";
import ChatWidget from "@/components/ChatWidget";
import {
  LandingHeader, LandingFooter, LandingPricing, LandingStickyCta,
  landingLogoVars, yearlyPlans,
} from "@/components/LandingChrome";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { themeFallbackClass } from "@/lib/storefrontTheme";
import { CreateStoreWithTheme } from "@/components/CarPartsOnboardDialog";
import { ThemeGlyph } from "@/lib/themeIcons";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowRight, Check, ChevronLeft, ChevronRight, Crown, Heart,
  LayoutGrid, LayoutList, Loader2, Sparkles, Headphones, Infinity, X,
} from "lucide-react";

const PAGE_SIZE = 6;
const FAV_KEY = "merastartup_theme_favs";
const SUGGESTIONS = [
  "E-commerce store",
  "SaaS landing page",
  "Restaurant website",
  "Portfolio design",
  "Coaching website",
];
const QUICK_FILTERS = [
  { id: "all", label: "All Templates" },
  { id: "popular", label: "Popular" },
  { id: "new", label: "New Arrivals" },
  { id: "premium", label: "Premium" },
  { id: "free", label: "Free" },
];

function loadFavs() {
  try {
    const raw = JSON.parse(localStorage.getItem(FAV_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function ThemeCard({ t, industry, preview, fav, onFav, compact = false, onSelect, selectLabel = "Select theme", testIdPrefix = "mp-theme" }) {
  return (
    <article
      className={`group rounded-[22px] bg-white border border-black/10 overflow-hidden shadow-sm hover:-translate-y-0.5 transition-transform flex ${compact ? "flex-row" : "flex-col"}`}
      data-testid={`${testIdPrefix}-${t.id}`}
    >
      <div className={`relative ${compact ? "w-36 sm:w-48 shrink-0 h-32 sm:h-40" : "h-56 sm:h-60"} ${themeFallbackClass(t.id)}`}>
        {(t.screenshot_url || t.thumbnail_url) ? (
          <img src={mediaUrl(t.screenshot_url || t.thumbnail_url)} alt={t.label} className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500" />
        ) : null}
        {onFav ? (
          <button type="button" onClick={() => onFav(t.id)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 shadow-sm flex items-center justify-center" aria-label="Save theme">
            <Heart className={`w-4 h-4 ${fav ? "fill-[#E85D04] text-[#E85D04]" : "text-stone-500"}`} />
          </button>
        ) : null}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-bold text-lg text-stone-900 leading-snug">{t.label} <span className="text-stone-400 font-medium">– {industry}</span></h3>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {(t.tags || []).slice(0, 3).map((tag) => (
            <span key={tag} className="text-[11px] font-semibold uppercase tracking-wide px-2.5 py-0.5 rounded-full border border-black/15 text-stone-600">{tag}</span>
          ))}
        </div>
        <p className="mt-3 text-sm text-stone-600 leading-relaxed line-clamp-2 flex-1">{t.description}</p>
        <div className="mt-5 flex gap-2.5">
          {preview ? (
            <a href={preview} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center h-10 rounded-full border border-black bg-white text-sm font-bold hover:bg-stone-50" data-testid={`mp-preview-${t.id}`}>Preview</a>
          ) : (
            <span className="flex-1 inline-flex items-center justify-center h-10 rounded-full border border-black/15 text-sm text-stone-400">Preview</span>
          )}
          {onSelect ? (
            <button type="button" onClick={() => onSelect(t)} data-testid={`${testIdPrefix}-select-${t.id}`} className="flex-1 inline-flex items-center justify-center h-10 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-[#FF8A2B]">
              {selectLabel}
            </button>
          ) : (
            <CreateStoreWithTheme themeId={t.id} testId={`mp-create-${t.id}`} className="flex-1 inline-flex items-center justify-center h-10 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-[#FF8A2B]">
              Create Store
            </CreateStoreWithTheme>
          )}
        </div>
      </div>
    </article>
  );
}

export default function ThemeMarketplace() {
  const cfg = useSiteConfig();
  const plans = yearlyPlans(cfg.pricing?.plans);
  const websitePrice = plans.find((p) => p.id === "website")?.price || plans[0]?.price || 1999;

  const [loading, setLoading] = useState(true);
  const [themes, setThemes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [query, setQuery] = useState("");
  const [pickedId, setPickedId] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiResults, setAiResults] = useState([]);
  const [categoryId, setCategoryId] = useState("all");
  const [quick, setQuick] = useState("all");
  const [sort, setSort] = useState("popular");
  const [view, setView] = useState("grid");
  const [page, setPage] = useState(1);
  const [favs, setFavs] = useState(loadFavs);
  const [showMoreCats, setShowMoreCats] = useState(false);

  useEffect(() => {
    api.get("/themes")
      .then(({ data }) => {
        setThemes(data.themes || []);
        setCategories(data.categories || []);
        setAiEnabled(!!data.ai_enabled);
      })
      .catch((e) => toast.error(extractErr(e)))
      .finally(() => setLoading(false));
  }, []);

  const catCount = (id) => themes.filter((t) => (t.category_ids || []).includes(id)).length;
  const badgeCount = (id) => (id === "all" ? themes.length : themes.filter((t) => t.badge === id).length);
  const catById = Object.fromEntries(categories.map((c) => [c.id, c]));
  const industryOf = (t) => (t.category_ids || []).map((id) => catById[id]?.name).filter(Boolean)[0] || "Storefront";

  const filtered = useMemo(() => {
    let list = [...themes];
    if (pickedId) {
      const hit = list.find((t) => t.id === pickedId);
      return hit ? [hit] : [];
    }
    if (categoryId !== "all") {
      list = list.filter((t) => (t.category_ids || []).includes(categoryId));
    }
    if (quick !== "all") {
      list = list.filter((t) => t.badge === quick);
    }
    if (sort === "az") list = [...list].sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    else if (sort === "new") list = [...list].sort((a, b) => (a.badge === "new" ? -1 : 0) - (b.badge === "new" ? -1 : 0));
    else list = [...list].sort((a, b) => (a.badge === "popular" ? -1 : 0) - (b.badge === "popular" ? -1 : 0));
    return list;
  }, [themes, pickedId, categoryId, quick, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const slice = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const start = filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const end = Math.min(safePage * PAGE_SIZE, filtered.length);

  const runSearch = async (text) => {
    const q = (text ?? query).trim();
    setQuery(q);
    if (q.length < 2) {
      toast.error("Describe the website you want in a few words.");
      return;
    }
    setAiOpen(true);
    setAiBusy(true);
    setAiResults([]);
    try {
      const { data } = await api.post("/themes/ai-search", { query: q });
      const order = data.theme_ids || [];
      const byId = Object.fromEntries(themes.map((t) => [t.id, t]));
      const ranked = order.map((id) => byId[id]).filter(Boolean);
      setAiResults(ranked.length ? ranked : themes);
      if (data.message) toast.message(data.message);
    } catch (e) {
      toast.error(extractErr(e));
      setAiResults([]);
    } finally {
      setAiBusy(false);
    }
  };

  const chooseTheme = (t) => {
    setPickedId(t.id);
    setCategoryId("all");
    setQuick("all");
    setPage(1);
    setAiOpen(false);
    requestAnimationFrame(() => {
      document.getElementById("theme-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const clearPick = () => {
    setPickedId(null);
    setPage(1);
  };

  const goPage = (n) => {
    const next = Math.min(Math.max(1, n), pages);
    setPage(next);
    requestAnimationFrame(() => {
      document.getElementById("theme-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const toggleFav = (id) => {
    setFavs((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
      return next;
    });
  };

  const visibleCats = showMoreCats ? categories : categories.slice(0, 6);
  const picked = themes.find((t) => t.id === pickedId);

  const activeChip = (on) => on
    ? "bg-[#FFEAD1] border-[#FF8A2B]/40 text-stone-900"
    : "bg-white border-black/10 text-stone-700";

  return (
    <div className="ms-landing min-h-screen font-sans antialiased overflow-x-hidden pb-16 md:pb-0" style={landingLogoVars(cfg)}>
      <SiteHead
        path="/theme"
        title="Storefront themes | Mera Startup"
        description="Browse Mera Startup storefront themes. Use AI search to match a layout to your business, then create your store."
      />
      <LandingHeader websitePrice={websitePrice} active="themes" />

      <section className="relative overflow-x-hidden border-b border-black/[0.06]" data-testid="theme-hero">
        <div className="absolute inset-0 pointer-events-none ms-hero-wash" />
        <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 py-12 lg:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex px-3 py-1 rounded-full bg-[#FFEAD1] text-[11px] font-bold">50+ LOOKS</span>
            <h1 className="ms-serif mt-4 text-3xl sm:text-4xl lg:text-[52px] leading-[0.98] font-bold tracking-tight text-stone-900">
              Discover the perfect template for your business
            </h1>
            <p className="mt-4 text-stone-600 text-base sm:text-lg max-w-md">
              Premium storefront themes. AI-powered search to find exactly what you need, faster.
            </p>
          </div>
          <form
            className="relative rounded-[22px] p-[1.5px] overflow-hidden shadow-sm"
            onSubmit={(e) => { e.preventDefault(); runSearch(); }}
            data-testid="theme-ai-search"
          >
            <div className="absolute inset-0 ms-aurora pointer-events-none opacity-80 rounded-[22px]" />
            <div className="relative rounded-[21px] bg-white p-4 sm:p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#c2410c]">
                <Sparkles className="w-4 h-4" /> AI-Powered Search
                {aiEnabled ? (
                  <span className="text-[10px] uppercase tracking-wide bg-[#E8F8F0] text-emerald-900 px-2 py-0.5 rounded-full">Live</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wide bg-[#FFEAD1] text-stone-700 px-2 py-0.5 rounded-full">Keyword</span>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-black/10 bg-[#FFFBF2] px-3 py-2 focus-within:border-stone-900">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Describe the website you want... e.g. 'fitness website with booking'"
                  className="flex-1 bg-transparent outline-none text-sm text-stone-800 placeholder:text-stone-400 min-w-0 py-2"
                  data-testid="theme-ai-input"
                />
                <button type="submit" disabled={aiBusy} className="w-11 h-11 rounded-full bg-stone-900 hover:bg-[#FF8A2B] text-white flex items-center justify-center shrink-0" data-testid="theme-ai-submit" aria-label="Search themes">
                  {aiBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => runSearch(s)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full border border-black/10 text-stone-600 hover:border-stone-900 hover:text-stone-900"
                    data-testid={`theme-suggest-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </form>
        </div>
      </section>

      <section className="border-b border-black/[0.06]" data-testid="theme-category-row">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-6 flex gap-3 overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => { setCategoryId("all"); setPickedId(null); setPage(1); }}
            className={`min-w-[148px] rounded-[18px] border px-4 py-3 text-left shrink-0 ${activeChip(categoryId === "all" && !pickedId)}`}
            data-testid="theme-cat-all"
          >
            <span className="w-9 h-9 rounded-xl bg-[#2ED4A6]/15 text-[#1B7A4A] inline-flex items-center justify-center mb-2"><LayoutList className="w-4 h-4" /></span>
            <div className="font-semibold text-sm">All Templates</div>
            <div className="text-xs text-stone-500">{themes.length}+</div>
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { setCategoryId(c.id); setPickedId(null); setPage(1); }}
              className={`min-w-[148px] rounded-[18px] border px-4 py-3 text-left shrink-0 ${activeChip(categoryId === c.id && !pickedId)}`}
              data-testid={`theme-cat-${c.id}`}
            >
              <span className="w-9 h-9 rounded-xl bg-[#2ED4A6]/15 text-[#1B7A4A] inline-flex items-center justify-center mb-2">
                <ThemeGlyph name={c.icon} className="w-4 h-4" />
              </span>
              <div className="font-semibold text-sm">{c.name}</div>
              <div className="text-xs text-stone-500">{catCount(c.id)}+</div>
            </button>
          ))}
        </div>
      </section>

      <section id="theme-results" className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10 scroll-mt-24">
        <div className="grid lg:grid-cols-[200px_minmax(0,1fr)] gap-8 lg:gap-10 items-start">
          <aside className="space-y-8 hidden lg:block" data-testid="theme-filters">
            <div>
              <h3 className="text-sm font-bold text-stone-900 mb-3">Categories</h3>
              <div className="space-y-1.5">
                {QUICK_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => { setQuick(f.id); setPickedId(null); setPage(1); }}
                    className={`w-full flex items-center justify-between text-sm px-2 py-1.5 rounded-lg ${quick === f.id && !pickedId ? "text-[#c2410c] font-semibold bg-[#FFEAD1]" : "text-stone-600 hover:bg-white"}`}
                    data-testid={`theme-quick-${f.id}`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <span className={`w-3.5 h-3.5 rounded-full border ${quick === f.id && !pickedId ? "border-[#FF8A2B] bg-[#FF8A2B]" : "border-stone-300"}`} />
                      {f.label}
                    </span>
                    <span className="text-xs text-stone-400">{badgeCount(f.id)}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold text-stone-900 mb-3">Industries</h3>
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-sm text-stone-600">
                  <input type="checkbox" checked={categoryId === "all"} onChange={() => { setCategoryId("all"); setPickedId(null); setPage(1); }} />
                  All Industries
                </label>
                {visibleCats.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm text-stone-600">
                    <input
                      type="checkbox"
                      checked={categoryId === c.id}
                      onChange={() => { setCategoryId(categoryId === c.id ? "all" : c.id); setPickedId(null); setPage(1); }}
                    />
                    {c.name}
                  </label>
                ))}
                {categories.length > 6 && (
                  <button type="button" className="text-xs text-[#c2410c] font-semibold mt-1" onClick={() => setShowMoreCats((v) => !v)}>
                    {showMoreCats ? "Show less" : "+ Show more"}
                  </button>
                )}
              </div>
            </div>
          </aside>

          <div>
            {picked ? (
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-black/10 bg-white px-4 py-3" data-testid="theme-picked-banner">
                <p className="text-sm text-stone-700">
                  Showing <span className="font-bold">{picked.label}</span> from your AI search{query ? ` for “${query}”` : ""}.
                </p>
                <button type="button" onClick={clearPick} className="text-sm font-bold text-[#c2410c] inline-flex items-center gap-1" data-testid="theme-picked-clear">
                  <X className="w-4 h-4" /> Show all templates
                </button>
              </div>
            ) : null}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <p className="text-sm text-stone-500" data-testid="theme-showing">
                {loading ? "Loading templates…" : `Showing ${start}–${end} of ${filtered.length} templates`}
              </p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setView("grid")} className={`w-9 h-9 rounded-lg border inline-flex items-center justify-center ${view === "grid" ? "border-stone-900 text-stone-900 bg-white" : "border-black/10 text-stone-500"}`} aria-label="Grid view"><LayoutGrid className="w-4 h-4" /></button>
                <button type="button" onClick={() => setView("list")} className={`w-9 h-9 rounded-lg border inline-flex items-center justify-center ${view === "list" ? "border-stone-900 text-stone-900 bg-white" : "border-black/10 text-stone-500"}`} aria-label="List view"><LayoutList className="w-4 h-4" /></button>
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 rounded-lg border border-black/10 text-sm px-2 text-stone-700 bg-white" data-testid="theme-sort">
                  <option value="popular">Most Popular</option>
                  <option value="new">Newest</option>
                  <option value="az">A–Z</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="py-16 text-center text-stone-400 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading themes…</div>
            ) : slice.length === 0 ? (
              <div className="py-16 text-center text-stone-500" data-testid="theme-empty">No themes match this filter. Try another category or search.</div>
            ) : (
              <div className={view === "list" ? "space-y-5" : "grid sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-7"} data-testid="theme-grid">
                {slice.map((t) => (
                  <ThemeCard
                    key={t.id}
                    t={t}
                    industry={industryOf(t)}
                    preview={t.preview_url}
                    fav={favs.includes(t.id)}
                    onFav={toggleFav}
                    compact={view === "list"}
                  />
                ))}
              </div>
            )}

            <div className="mt-8 flex items-center justify-center gap-2" data-testid="theme-pagination">
              <button type="button" disabled={safePage <= 1} onClick={() => goPage(safePage - 1)} className="w-9 h-9 rounded-lg border border-black/10 inline-flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => goPage(n)}
                  className={`w-9 h-9 rounded-lg text-sm font-semibold ${n === safePage ? "bg-stone-900 text-white" : "border border-black/10 text-stone-600"}`}
                >
                  {n}
                </button>
              ))}
              <button type="button" disabled={safePage >= pages} onClick={() => goPage(safePage + 1)} className="w-9 h-9 rounded-lg border border-black/10 inline-flex items-center justify-center disabled:opacity-40" aria-label="Next page">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-6">
        <div className="rounded-[22px] bg-[#FFEAD1] border border-black/10 px-5 py-6 sm:px-8 flex flex-col lg:flex-row lg:items-center gap-5">
          <div className="flex items-start gap-3 flex-1">
            <span className="w-10 h-10 rounded-full bg-[#FF8A2B] text-white inline-flex items-center justify-center shrink-0"><Crown className="w-5 h-5" /></span>
            <div>
              <div className="font-bold text-stone-900">Unlock Premium Templates</div>
              <p className="text-sm text-stone-600">Get access to every storefront theme and future releases with your yearly plan.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-semibold text-stone-700">
            <span className="inline-flex items-center gap-1.5"><LayoutList className="w-4 h-4 text-[#c2410c]" /> All storefronts</span>
            <span className="inline-flex items-center gap-1.5"><Infinity className="w-4 h-4 text-[#c2410c]" /> Lifetime updates</span>
            <span className="inline-flex items-center gap-1.5"><Headphones className="w-4 h-4 text-[#c2410c]" /> Priority support</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#c2410c]" /> Cancel anytime</span>
          </div>
        </div>
      </section>

      <LandingPricing pricing={cfg.pricing} plans={plans} />
      <LandingFooter />
      <LandingStickyCta websitePrice={websitePrice} />
      <ChatWidget />

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-[22px] border-black/10 bg-[#FFFBF2] p-5 sm:p-6" data-testid="theme-ai-popup">
          <DialogTitle className="ms-serif text-2xl font-bold text-stone-900 pr-8">
            Templates that match your search
          </DialogTitle>
          <p className="text-sm text-stone-600 -mt-1">{query ? `Results for “${query}”` : "Pick a storefront to continue."}</p>
          {aiBusy ? (
            <div className="py-16 text-center text-stone-500 flex items-center justify-center gap-2" data-testid="theme-ai-popup-loading">
              <Loader2 className="w-5 h-5 animate-spin" /> Finding the best templates…
            </div>
          ) : aiResults.length === 0 ? (
            <div className="py-10 text-center text-stone-500" data-testid="theme-ai-popup-empty">No matching templates. Try a different description.</div>
          ) : (
            <div className="space-y-4" data-testid="theme-ai-popup-list">
              {aiResults.map((t) => (
                <ThemeCard
                  key={t.id}
                  t={t}
                  industry={industryOf(t)}
                  preview={t.preview_url}
                  compact
                  onSelect={chooseTheme}
                  selectLabel="Select theme"
                  testIdPrefix="mp-suggest"
                />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
